import { defineStore } from "pinia";

const apiUrl = import.meta.env.VITE_API_URL;
const siteBaseUrl = "https://www.greenmedicineworks.com";
const defaultDescription =
  "Science Based Encyclopedia of Nutrition and Natural Health";

export interface PreviewSeo {
  title?: string;
  description?: string;
  keywords?: string;
  ogtitle?: string;
  ogdescription?: string;
  url: string;
}

export interface RelatedTopic {
  id: string;
  title: string;
}

export interface PreviewEntry {
  found: boolean;
  seoData: string;
  title: string;
  description: string;
  seo: PreviewSeo;
  jsonLd: object | null;
  error?: string;
  /** False when the topic is short enough to be published in full. */
  isTruncated: boolean;
  /** Share of the article's text in `seoData`, 0-100. */
  previewPercent: number;
  /** Names of the withheld sections, for the paywall to list by name. */
  lockedSections: string[];
  /** Total withheld sections; `lockedSections` is capped at six. */
  lockedSectionCount: number;
  /** Illustrations held back because they were too heavy to inline. */
  strippedImages: number;
  /** Cross-linked topics, rendered as onward navigation. */
  relatedTopics: RelatedTopic[];
  readingMinutes: number;
  totalWords: number;
}

/** A miss — a bad id, an API error, or an offline client. */
function notFound(url: string, error: string): PreviewEntry {
  return {
    found: false,
    seoData: "",
    title: "",
    description: "",
    seo: { url },
    jsonLd: null,
    error,
    isTruncated: false,
    previewPercent: 0,
    lockedSections: [],
    lockedSectionCount: 0,
    strippedImages: 0,
    relatedTopics: [],
    readingMinutes: 0,
    totalWords: 0,
  };
}

// The preview HTML is stored RAW here: the Node prerender has no DOM for
// DOMPurify, and the raw value is what gets serialized into the prerendered
// HTML for crawlers. It is sanitized at the render boundary on the client —
// see `seoData` in src/pages/preview/[id]/index.vue — so DOMPurify still runs
// before any browser inserts it via v-html.

export const usePreviewStore = defineStore("preview", {
  state: () => ({
    // Keyed by preview id. Populated during onServerPrefetch (server) or
    // onMounted/navigation (client). Serialized into the prerendered HTML
    // via Pinia initialState so the client hydrates without refetching.
    cache: {} as Record<string, PreviewEntry>,
  }),

  actions: {
    async load(id: string): Promise<PreviewEntry> {
      if (this.cache[id]) return this.cache[id];

      const url = `${siteBaseUrl}/preview/${id}`;
      try {
        const response = await fetch(`${apiUrl}/search/${id}`, {
          method: "GET",
          credentials: "include" as RequestCredentials,
          headers: { "Content-Type": "application/json" },
        });

        if (response.status === 200) {
          const data = await response.json();
          const seoTitle =
            data.r_seo_title != null && data.r_seo_title !== ""
              ? data.r_seo_title
              : data.r_title;

          // The API caps the public excerpt (see server utilities/
          // previewExcerpt.js). Older deployments answer without these fields,
          // so every one of them falls back to "nothing was withheld" rather
          // than rendering a paywall over content that is actually complete.
          const isTruncated = data.r_is_truncated === true;

          const entry: PreviewEntry = {
            found: true,
            seoData: data.r_data || "",
            title: data.r_title || "",
            description: data.r_description || "",
            isTruncated,
            previewPercent: data.r_preview_percent ?? 100,
            lockedSections: data.r_locked_sections ?? [],
            lockedSectionCount: data.r_locked_section_count ?? 0,
            strippedImages: data.r_stripped_images ?? 0,
            relatedTopics: (data.r_related_topics ?? []).map(
              (t: { r_id: number; r_title: string }) => ({
                id: String(t.r_id),
                title: t.r_title,
              })
            ),
            readingMinutes: data.r_reading_minutes ?? 1,
            totalWords: data.r_total_words ?? 0,
            seo: {
              title: seoTitle,
              description: data.r_description,
              keywords: data.r_keyword,
              ogtitle: data.r_ogtitle,
              ogdescription: data.r_ogdescription,
              url,
            },
            jsonLd: {
              "@context": "https://schema.org",
              "@type": "MedicalWebPage",
              headline: data.r_title,
              name: data.r_title,
              description: data.r_description || defaultDescription,
              url,
              dateModified: data.r_modified_date || undefined,
              wordCount: data.r_total_words || undefined,
              publisher: {
                "@type": "Organization",
                name: "Green Medicine Works",
                url: siteBaseUrl,
              },
              speakable: {
                "@type": "SpeakableSpecification",
                cssSelector: ["h1", ".seo-preview"],
              },
              // Declare the paywall honestly, and only when one is actually
              // rendered. Google reads `hasPart` to tell a legitimate metered
              // preview from cloaking, and the selector has to resolve to a
              // real element on the page — `.paywall-gate` is the gate card in
              // pages/preview/[id]/index.vue. Claiming a paywall on a topic
              // published in full is the kind of mismatch that gets a site
              // demoted, so short topics declare themselves free.
              isAccessibleForFree: !isTruncated,
              ...(isTruncated
                ? {
                    hasPart: {
                      "@type": "WebPageElement",
                      isAccessibleForFree: false,
                      cssSelector: ".paywall-gate",
                    },
                  }
                : {}),
            },
          };
          // Cache only successful results, so a bad id prerenders as a
          // loading shell and the client can refetch / redirect to /error.
          this.cache[id] = entry;
          return entry;
        }

        let message = "Error on getting content";
        try {
          const verror = JSON.parse(await response.text());
          message = verror?.error?.detail || verror?.error?.message || message;
        } catch {
          /* non-JSON error body */
        }
        return notFound(url, message);
      } catch (error) {
        return notFound(
          url,
          error instanceof Error ? error.message : "Network error"
        );
      }
    },
  },
});
